<template>
  <div>
    <b-modal
      id="modal-task-group-create"
      ref="modal_task_group_create"
      title="Новая группа заявок"
      size="xl"
      hide-header-close
      content-class="modal-fullscreen"
      body-class="modal-body-fullscreen"
      modal-class="custom-modal"
      lazy
      centered
      scrollable
      no-close-on-backdrop
      no-close-on-esc
      @hidden="onHidden"
    >
      <b-tabs v-model="tabIndex"  vertical class="custom-tabs stepper-vertical">
        <b-tab active>
          <template #title>
            <div class="step-item" :class="{ 'is-completed': isStep1Complete }">
              <div class="step-icon" :class="{ 'is-completed': isStep1Complete }">
                <v-icon v-if="isStep1Complete" name="buttons/check" :size="12"/>
                <span v-else>1</span>
              </div>
              <div class="step-label">
                <span class="step-title">Информация</span>
              </div>
            </div>
          </template>

          <b-row class="mb-4">
            <b-col cols="6">
              <b-form-group id="group-location" label="Объект">
                <location-select
                  id="input-location-select"
                  ref="location_select"
                  :value="newItem.location_id"
                  :state="validationState.location_id"
                  @onSelect="onLocationChange"
                />
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group id="group-start-address" :label="$t('message.tasks.modal.start_address')">
                <b-form-input
                  id="input_start_address"
                  type="text"
                  :state="validationState.start_address"
                  :placeholder="$t('message.tasks.modal.start_addressPlaceholder')"
                  v-model="newItem.start_address"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mb-4">
            <b-col cols="6">
              <b-form-group id="group-executor" label="Менеджер">
                <user-select
                  id="input-user-select"
                  class="mr-2 flex-fill"
                  ref="user_select"
                  :state="validationState.executor"
                  :value="newItem.executor"
                  :is-disabled="newItem.location_id === 0 || noLocationDisabled"
                  @onSelect="onSelectExecutor"
                />
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group id="group-project" label="Руководитель">
                <b-form-input
                  id="input-project"
                  readonly
                  :value="newItem.project_name || '-'"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mb-4">
            <b-col cols="8">
              <b-form-group id="group-work-type" label="Вид работ объекта">
                <location-works-select
                  id="location-works-select"
                  ref="location_works_select"
                  :state="validationState.task_location_works"
                  :is-disabled="newItem.location_id === 0 || noLocationDisabled"
                  @onSelect="onLocationWorkSelect"
                />
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group id="group-workers-count" label="Количество исполнителей">
                <b-form-input
                  id="input-workers-count"
                  type="number"
                  min="0"
                  :state="validationState.plan_workers_count"
                  v-model.number="newItem.plan_workers_count"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="12">
              <b-form-group id="group-workers">
                <template #label>
                  <div class="d-flex align-items-center">
                    <span>Исполнители</span>
                    <b-badge
                      v-if="newItem.task_workers.length"
                      class="ml-2 mt-1 task-group-badge"
                    >
                      {{ newItem.task_workers.length }} чел.
                    </b-badge>
                  </div>
                </template>

                <div class="form-control custom-tags-input position-relative d-flex">
                  <div v-if="newItem.task_workers.length" class="tags-wrapper d-flex flex-wrap align-items-center">
                    <span
                      v-for="worker in newItem.task_workers"
                      :key="worker.id"
                      :class="['custom-tag', getLevelEfficiencyStyle(worker.level_efficiency)]"
                    >
                      <span class="text-truncate" style="max-width: 180px;">{{ worker.name }}</span>
                        <v-icon name="buttons/cross" :size="8" class="cursor-pointer ml-2" @click.stop="removeWorker(worker.worker_id)"/>
                      </span>
                  </div>
                  <div v-else class="text-muted">
                    Исполнители не выбраны
                  </div>
                  <v-icon v-if="newItem.task_workers.length" name="buttons/cross" :size="12" class="clear-all-icon" @click.stop="clearAllWorkers"/>
                </div>

                <button type="button" class="custom-btn-dashed mt-2" @click="showWorkersSelect">
                  <v-icon name="buttons/plus" :size="11"/>
                  Добавить исполнителей
                </button>
              </b-form-group>
            </b-col>
          </b-row>
        </b-tab>

        <b-tab>
          <template #title>
            <div class="step-item" :class="{ 'is-completed': isStep2Complete }">
              <div class="step-icon" :class="{ 'is-completed': isStep2Complete }">
                <v-icon v-if="isStep2Complete" name="buttons/check" :size="12" />
                <span v-else>2</span>
              </div>
              <div class="step-label">
                <span v-if="newItem.task_workers.length !== 0" class="step-title">Табель</span>
                <span v-else class="step-title">Календарь</span>
              </div>
            </div>
          </template>

          <div class="w-100 d-flex flex-column flex-grow-1" style="min-height: 0;">
            <div v-if="newItem.task_workers.length !== 0" class="d-flex flex-column flex-grow-1" style="min-height: 0;">
              <task-timesheet
                class="flex-grow-1"
                ref="timesheet"
                :base-task="newItem"
                :services="locationWorkServices"
                :scroll-locked="onboardingStep > 0"
                @change="updateTimesheetTasks"
                @workers-planned="plannedWorkersCount = $event"
              />
            </div>

            <div v-else class="d-flex flex-column flex-grow-1" style="min-height: 0;">
              <div class="d-flex align-items-center mb-3">
                <select v-model="currentMonth" class="custom-select-filters mr-2">
                  <option v-for="month in availableMonths" :key="month.index" :value="month.index">{{ month.name }}</option>
                </select>

                <select v-model="currentYear" class="custom-select-filters">
                  <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="calendar-grid header">
                <div v-for="day in weekDays" :key="day" class="weekday-cell">
                  {{ day }}
                </div>
              </div>

              <div class="calendar-grid">
                <div
                  v-for="(day, index) in calendarDays"
                  :key="day.dateString"
                  class="date-cell"
                  :class="{
                      'is-not-current-month': !day.isCurrentMonth,
                      'is-selected': isDateSelected(day.dateString)
                    }"
                  @click="toggleDate(day.dateString)"
                >
                  <span class="day-number">{{ day.dayNumber }}</span>
                  <span
                    v-if="day.dayNumber === 1 || index === 0"
                    class="month-label text-truncate"
                  >
                      {{ getMonthNameByDate(day.date) }}
                    </span>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab>
          <template #title>
            <div class="step-item">
              <div class="step-icon">3</div>
              <div class="step-label">
                <span class="step-title">Проверка</span>
              </div>
            </div>
          </template>

          <b-form-group id="group-task-group" label="Группа">
            <task-group-select
              :value="newItem.group_id"
              @onSelect="onSelectTaskGroup"
            />
          </b-form-group>

          <div class="mt-4">
            <h5 class="summary-title mb-4">Информация</h5>
            <div class="summary-list">
              <div class="summary-item">
                <span class="summary-label">Объект:</span>
                <span class="summary-value">{{ newItem.location_name || '—' }}</span>
              </div>

              <div class="summary-item">
                <span class="summary-label">Адрес:</span>
                <span class="summary-value">{{ newItem.start_address || '—' }}</span>
              </div>

              <div class="summary-item">
                <span class="summary-label">Менеджер:</span>
                <span class="summary-value">{{ newItem.executor_name || '—' }}</span>
              </div>

              <div class="summary-item">
                <span class="summary-label">Руководитель:</span>
                <span class="summary-value">{{ newItem.project_name || '—' }}</span>
              </div>

              <div class="summary-item">
                <span class="summary-label">Вид работ:</span>
                <span class="summary-value">{{ newItem.task_location_works.length ? newItem.task_location_works[0].work_type_name : '—' }}</span>
              </div>

              <div class="summary-item">
                <span class="summary-label">Исполнители:</span>
                <div class="summary-value">
                  <template v-if="newItem.task_workers.length > 25">
                    <span
                      class="text-primary cursor-pointer"
                      @click="$bvModal.show('modal-workers-list')"
                    >
                      Список исполнителей
                    </span>
                  </template>
                  <template v-else>
                    {{ workerNames }}
                  </template>
                </div>
                <b-badge
                  v-if="newItem.task_workers.length"
                  class="task-group-badge"
                >
                  {{ newItem.task_workers.length }} чел.
                </b-badge>
              </div>

              <div class="summary-item">
                <span class="summary-label">Будет создано заявок:</span>
                <span v-if="newTasksCount > 0" class="summary-value">{{ newTasksCount }}</span>
                <span v-else class="summary-value">—</span>
              </div>

              <div class="summary-item">
                <span class="summary-label">Даты новых заявок:</span>
                <div class="summary-value">
                  <div v-if="selectedTaskDates.length > 0" class="d-flex flex-wrap justify-content-end" style="gap: 5px;">
                    <b-badge
                      v-for="date in selectedTaskDates"
                      :key="date"
                      class="task-group-badge"
                    >
                      {{ date }}
                    </b-badge>
                  </div>
                  <span v-else>—</span>
                </div>
              </div>
            </div>
          </div>

          <b-modal id="modal-workers-list" title="Список исполнителей" hide-footer scrollable>
            <div v-for="(worker, idx) in newItem.task_workers" :key="worker.id || idx" class="mb-2 pb-2 border-bottom">
              <span class="text-muted mr-1">{{ idx + 1 }}.</span> {{ worker.name }}
            </div>
          </b-modal>
        </b-tab>
      </b-tabs>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <b-button v-if="newItem.task_workers.length !== 0 && tabIndex === 1" class="custom-btn-secondary mr-2" @click="handleHelpToggle">
            <v-icon name="buttons/question_circle" :size="16"/>
          </b-button>
          <b-button class="custom-btn-secondary mr-2" @click="hideModal">
            Отменить
          </b-button>
          <b-button v-if="tabIndex < 2" class="custom-btn-primary" @click="nextStep">
            Далее
          </b-button>
          <b-button v-else class="custom-btn-primary" @click="handleSubmit">
            Сохранить
          </b-button>
        </div>
      </template>
    </b-modal>

    <workers-free
      ref="workers_select"
      :workers="newItem.task_workers"
      @addToTask="onAddWorkers"
    />

    <onboarding
      v-if="onboardingStep > 0 && showOnboarding"
      :step="onboardingStep"
      :steps-config="onboardingConfig"
      @next="handleNextStep"
      @close="onboardingStep = 0"
      @disable="disableOnboarding"
    />
  </div>
</template>

<script>
import tasks from "../../network/tasks";
import users from "../../network/users";
import customers from "../../network/customers";
import locations from "../../network/locations";
import catalogs from "../../network/catalogs";
  import permissions, {PERM} from "../../utilites/permissions";
  import dateFormatter from '../../utilites/dateFormatter'
  import taskService from './taskService';
  import helpers from "../../utilites/helpers";
    import LocationSelect from "../Locations/LocationSelect.vue";
    import LocationWorksSelect from "../Locations/LocationWorksSelect.vue";
    import UserSelect from "../Users/UserSelect.vue";
    import WorkersFree from "../Workers/WorkersFree.vue";
    import TaskTimesheet from "./TaskTimesheet.vue";
    import TaskGroupSelect from "../Catalogs/TaskGroups/TaskGroupSelect.vue";
import Onboarding from "../../utilites/commonUi/Onboarding.vue";

const WEEK_DAYS =['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTH_NAMES =  ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

const ONBOARDING_STEPS = [
  { id: 2, target: 'target-cell-1', content: 'Чтобы назначить исполнителя, кликните на ячейку напротив даты и исполнителя. Затем нажмите кнопку "+", чтобы назначить его в заявку.' },
  { id: 3, target: 'target-cell-5', content: 'При клике на дату в заявку на этот день добавляются все исполнители из списка слева.' },
  { id: 4, target: 'price-target', content: 'Чтобы назначить всем исполнителям единую ставку, кликните на заголовок колонки "Ставка".' },
  { id: 5, target: 'planning-btn', content: 'После назначения исполнителей на заявки, запланируйте им объём работ и другие параметры.' },
  { id: 6, target: 'remove-btn', content: 'Чтобы убрать исполнителей из заявок, нажмите на иконку корзины и выделите рамкой или кликом нужные ячейки.' },
  { id: 7, target: 'target-cell-1', content: 'Чтобы запланировать нескольким исполнителям сразу на несколько заявок, выберите нужные ячейки выделением рамкой (зажать левую кнопку мыши и потянуть мышь).' },
  { id: 8, target: 'calc-individual', content: 'Чтобы посмотреть расчёты по отдельным заявкам, перейдите в режим "отдельные расчёты" и выберите нужную дату заявки, куда уже назначены и запланированы исполнители.' },
];

export default {
  name: 'TaskGroupCreate',
  components: {Onboarding, TaskGroupSelect, TaskTimesheet, WorkersFree, LocationWorksSelect, UserSelect, LocationSelect },
  mixins: [
    tasks, catalogs, users, customers, locations,
    permissions, dateFormatter, taskService, helpers
  ],
  data() {
    return {
      tabIndex: 0,
      newItem: this.setNewItem(),
      isBusy: false,
      noLocationDisabled: false,
      locationWorkServices: [],
      location: null,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      weekDays: WEEK_DAYS,
      plannedWorkersCount: 0,
      timesheetTasks: [],
      calendarTasks: [],
      onboardingStep: 0,
      showOnboarding: !localStorage.getItem('hide_tasks_create_onboarding'),
      onboardingConfig: ONBOARDING_STEPS,
      author: { id: 0, name: '' },
    }
  },
  async mounted() {
    this.author = (await this.getUserInfo()).data;
  },
  watch: {
    currentYear() {
      if (!this.availableMonths.some(m => m.index === this.currentMonth)) {
        this.currentMonth = new Date().getMonth();
      }
    },
    tabIndex(newVal) {
      if (newVal === 1 && this.showOnboarding && this.newItem.task_workers.length > 0) {
        this.showOnboardingModal();
      }
    },
    'newItem.task_workers.length'(newValue) {
      if (newValue > 0) {
        this.calendarTasks = [];
      } else {
        this.timesheetTasks = [];
      }
    },
  },
  computed: {
    selectedTaskDates() {
      let dates = [];

      if (this.newItem.task_workers.length > 0) {
        const uniqueDates = new Set(
          this.timesheetTasks.map(key => key.split('_')[1])
        );
        dates = Array.from(uniqueDates);
      } else {
        dates = [...this.calendarTasks];
      }

      return dates.sort((a, b) => new Date(a) - new Date(b));
    },
    validationState() {
      const item = this.newItem;
      return {
        location_id: item.location_id > 0,
        start_address: !!(item.start_address && item.start_address.trim()),
        executor: item.executor > 0,
        task_location_works: item.task_location_works && item.task_location_works.length > 0,
        plan_workers_count: item.plan_workers_count > 0
      }
    },
    calendarDays() {
      const year = this.currentYear;
      const month = this.currentMonth;

      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);
      const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;

      const days = [];

      for (let i = firstDayIndex; i > 0; i--) {
        const d = new Date(year, month, 1 - i);
        days.push(this.formatDayObject(d, false));
      }


      for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
        const d = new Date(year, month, i);
        days.push(this.formatDayObject(d, true));
      }


      const totalCells = days.length > 35 ? 42 : 35;
      const remaining = totalCells - days.length;
      for (let i = 1; i <= remaining; i++) {
        const d = new Date(year, month + 1, i);
        days.push(this.formatDayObject(d, false));
      }

      return days;
    },
    availableYears() {
      const current = new Date().getFullYear();
      return [current, current + 1];
    },
    availableMonths() {
      const year = new Date().getFullYear();
      const month = new Date().getMonth();
      const months = [];

      for (let i = 0; i < 12; i++) {
        if (this.currentYear > year || i >= month - 1) {
          months.push({ index: i, name: MONTH_NAMES[i] });
        }
      }
      return months;
    },
    workerNames() {
      const workers = this.newItem.task_workers;

      if (!workers || !workers.length) return 'Не выбраны';

      if (workers.length > 25) return '';
      return workers.map(w => w.name).join(', ');
    },
    isStep1Complete() {
      const v = this.validationState;
      return v.location_id && v.start_address && v.executor && v.task_location_works && v.plan_workers_count;
    },
    isStep2Complete() {
      if (this.newItem.task_workers.length > 0) {
        return this.timesheetTasks.length > 0 &&
          this.timesheetTasks.length === this.plannedWorkersCount;
      }

      return this.calendarTasks && this.calendarTasks.length > 0;
    },
    newTasksCount() {
      if (this.newItem.task_workers.length > 0) {
        if (!this.timesheetTasks || this.timesheetTasks.length === 0) return 0;

        const uniqueDates = new Set(
          this.timesheetTasks.map(key => key.split('_')[1])
        );
        return uniqueDates.size;
      }

      return this.calendarTasks ? this.calendarTasks.length : 0;
    },
  },
  methods: {
    updateTimesheetTasks(selectedCells) {
      this.timesheetTasks = selectedCells;
    },
    formatDayObject(date, isCurrentMonth) {
      return {
        date: date,
        dayNumber: date.getDate(),
        isCurrentMonth: isCurrentMonth,
        dateString: this.formatDate(date)
      };
    },
    getMonthNameByDate(date) {
      return MONTH_NAMES[date.getMonth()].toLowerCase();
    },
    isDateSelected(dateString) {
      return this.calendarTasks.includes(dateString);
    },
    toggleDate(dateString) {
      const index = this.calendarTasks.indexOf(dateString);

      if (index === -1) {
        this.calendarTasks.push(dateString);
      } else {
        this.calendarTasks.splice(index, 1);
      }
    },
    handleHelpToggle() {
      if (this.onboardingStep !== 0) {
        this.onboardingStep = 0;
        return;
      }

      this.showOnboarding = true;
      this.showOnboardingModal();
    },
    handleNextStep() {
      const currentIndex = this.onboardingConfig.findIndex(s => s.id === this.onboardingStep);
      this.onboardingStep = currentIndex !== -1 && currentIndex < this.onboardingConfig.length - 1 ? this.onboardingConfig[currentIndex + 1].id : 0;
    },
    disableOnboarding() {
      localStorage.setItem('hide_tasks_create_onboarding', 'true');
      this.showOnboarding = false;
      this.onboardingStep = 0;
    },
    setNewItem() {
      return {
        id: null,
        start_address: '',
        end_address: '',
        start_date: '',
        end_date: '',
        payment_type_int: 0,
        payment_first: 250,
        payment_second: 0,
        payment_first_count: 0,
        payment_second_count: 0,
        total_hours: 0,
        total_price: 0,
        total_worker_price: 0,
        total_worker_price_with_comiss: 0,
        total_delta: 0,
        total_manager: 0,
        declarated_cost: 0,
        additional_cost: 0,
        management_cost: 0,
        smz_cost: 0,
        additional_income: 0,
        customer_id: 0,
        customer_name: '',
        executor: 0,
        executor_name: '',
        executor_rate: 0,
        project_id: null,
        project_name: '',
        project_rate: null,
        author: 0,
        author_name: '',
        location_id: 0,
        location_name: '',
        status_id: 1,
        status_name: '',
        invoice_id: null,
        invoice_numb: null,
        town_id: null,
        work_type_id: 1,
        unit_code: "539",
        paid: false,
        read_only: false,
        description: '',
        cargo_description: '',
        contact: '',
        plan_workers_count: 0,
        total_project: null,
        task_workers: [],
        task_location_works: [],
        profit_tax: 0,
        nds: 0,
        transfer_commission: 0,
        bank_commission: 0,
        ip_commission: 0,
        group_id: null,
        group_name: '',
      }
    },
    showModal() {
      this.$refs.modal_task_group_create.show();
    },
    hideModal() {
      this.$refs.modal_task_group_create.hide();
    },
    async handleSubmit() {
      const v = this.validationState;

      if (!v.location_id) return this.showSubmitError("Выберите объект.");
      if (!v.start_address) return this.showSubmitError("Заполните адрес заявки.");
      if (!v.executor) return this.showSubmitError("Выберите менеджера.");
      if (!v.task_location_works) return this.showSubmitError("Выберите вид работ объекта.");
      if (!v.plan_workers_count) return this.showSubmitError("Планируемое количество исполнителей должно быть больше нуля.");

      if (this.newItem.task_workers.length > 0) {
        const timesheetData = this.$refs.timesheet ? this.$refs.timesheet.taskWorkerCells : {};
        const plannedKeys = Object.keys(timesheetData);

        if (this.timesheetTasks.length === 0) {
          return this.showSubmitError("Назначьте исполнителям смены в табеле.");
        }

        if (this.timesheetTasks.length !== plannedKeys.length) {
          return this.showSubmitError("Необходимо запланировать данные по всем датам для каждого выбранного исполнителя в табеле.");
        }
      }

      if (this.newItem.task_workers.length === 0 && this.calendarTasks.length === 0) {
        return this.showSubmitError("Выберите дату заявки в календаре.");
      }

      this.isBusy = true;

      if (this.newItem.author === 0) {
        this.newItem.author = this.author.id;
        this.newItem.author_name = this.author.name;
      }

      const tasks = [];

      if (this.newItem.task_workers.length > 0) {
        const timesheetData = this.$refs.timesheet ? this.$refs.timesheet.taskWorkerCells : {};
        const selectedKeys = Object.keys(timesheetData);

        const groupedByDate = selectedKeys.reduce((acc, key) => {
          const [workerId, date] = key.split('_');

          if (!acc[date]) acc[date] = [];
          acc[date].push({workerId: parseInt(workerId), timesheetData: timesheetData[key]});
          return acc;
        }, {});

        for (const [date, workersInTask] of Object.entries(groupedByDate)) {

          const taskItem = JSON.parse(JSON.stringify(this.newItem));
          const firstWorkerPlan = workersInTask[0].timesheetData;

          taskItem.start_date = this.formatServerDateTime(date, firstWorkerPlan.start_time);
          taskItem.end_date = this.formatServerDateTime(firstWorkerPlan.end_date || date, firstWorkerPlan.end_time);
          taskItem.task_workers = [];

          for (const task of workersInTask) {
            const baseWorker = this.newItem.task_workers.find(w => w.worker_id === task.workerId);
            if (!baseWorker) continue;

            const workerId = baseWorker.worker_id;

            let actualPrice = 0;
            if (this.$refs.timesheet && this.$refs.timesheet.workerPrices) {
              actualPrice = Number(this.$refs.timesheet.workerPrices[workerId]) || 0;
            } else {
              actualPrice = Number(task.timesheetData.price) || 0;
            }

            const tw = {
              ...baseWorker,
              start_date: this.formatServerDateTime(date, task.timesheetData.start_time),
              end_date: this.formatServerDateTime(task.timesheetData.end_date || date, task.timesheetData.end_time),
              start_time: task.timesheetData.start_time,
              end_time: task.timesheetData.end_time,

              total_units: task.timesheetData.total_units,
              dinner: task.timesheetData.dinner,
              price: actualPrice,
              road_price: task.timesheetData.road_price !== undefined ? task.timesheetData.road_price : baseWorker.road_price,
              prepaid: task.timesheetData.prepaid !== undefined ? task.timesheetData.prepaid : baseWorker.prepaid,

              task_worker_services: task.timesheetData.task_worker_services && task.timesheetData.task_worker_services.length > 0
                ? JSON.parse(JSON.stringify(task.timesheetData.task_worker_services))
                : []
            };

            if (taskItem.unit_code === '539') {
              tw.total_units = this.getTotalHours(tw);
            }
            tw.total_price = this.calculateTotalWorkerPrice(tw);
            taskItem.task_workers.push(tw);
          }

          taskItem.workers_count = taskItem.task_workers.length;
          this.updateTotals(taskItem);
          tasks.push(taskItem);
        }
      } else {
        for (const dateString of this.calendarTasks) {
          const taskItem = JSON.parse(JSON.stringify(this.newItem));
          const formattedDateTime = this.formatServerDateTime(dateString, this.formatTime(new Date()));

          taskItem.start_date = formattedDateTime;
          taskItem.end_date = formattedDateTime;
          taskItem.task_workers = [];

          tasks.push(taskItem);
        }
      }

      const result = await this.postTasksBatch(tasks);

      if (!result || !result.data) {
        this.isBusy = false;
        return this.showSubmitError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
      }

      this.$bvToast.toast('Группа заявок успешно создана.', { title: 'Успех', variant: 'success', solid: true });
      this.$emit('refresh');
      this.hideModal();
      this.isBusy = false;
    },
    updateTotals(task) {
      task.total_worker_price = this.calculateWorkersPrice(task.task_workers);

      const smzFactor = task.smz_cost > 0 ? (task.smz_cost / 100) : 0;
      task.total_worker_price_with_comiss = task.total_worker_price * (1 + smzFactor);

      task.total_hours = this.calculateWorkersHours(task.task_workers);
      task.total_price = this.calculateTotalPrice(task);
      task.total_delta = this.calculateTotalDelta(task);

      const rateType = task.rate_type || 1;
      task.total_manager = rateType === 2
        ? this.calculateTotalDeltaWithRateUnits(task.total_hours, task.executor_rate || 0)
        : this.calculateTotalDeltaWithRatePercent(task.total_delta, task.executor_rate || 0);

      if (task.project_id) {
        task.total_project = rateType === 2
          ? Math.round(task.total_hours * (task.project_rate || 0) * 100) / 100
          : Math.round((task.total_delta - task.total_manager) * ((task.project_rate || 0) / 100) * 100) / 100;
      } else {
        task.total_project = 0;
      }
    },
    onHidden() {
      this.newItem = this.setNewItem();
      this.locationWorkServices = [];
      this.timesheetTasks = [];
      this.calendarTasks = [];
      this.tabIndex = 0;
      this.onboardingStep = 0;
      this.$emit('modal-closed');
    },
    nextStep() {
      this.tabIndex++;
    },
    async onLocationChange(locationId) {
      if (locationId === this.newItem.location_id) return;

      if (locationId === 0) {
        await this.onClearLocation();
        return;
      }

      this.noLocationDisabled = true;
      const locationResult = await this.getLocationById(locationId);

      if (!locationResult || !locationResult.data) {
        this.noLocationDisabled = false;
        return;
      }

      const location = locationResult.data;

      this.newItem.location_id = location.id;
      this.newItem.location_name = location.name;
      this.newItem.town_id = location.town_id;
      this.newItem.payment_type_int = location.payment_type;
      this.newItem.management_cost = location.management_cost;
      this.newItem.smz_cost = location.smz_cost;
      this.newItem.profit_tax = location.profit_tax;
      this.newItem.nds = location.nds;
      this.newItem.transfer_commission = location.transfer_commission;
      this.newItem.bank_commission = location.bank_commission;
      this.newItem.ip_commission = location.ip_commission;

      if (!this.newItem.start_address) {
        this.newItem.start_address = location.address;
      }

      if (location.location_work_services) {
        this.locationWorkServices = location.location_work_services.map(item => ({
          value: item.id,
          text: item.work_service_name,
          data: item
        }));
      }

      if (this.$refs.user_select) {
        await this.$refs.user_select.configure(this.newItem.executor, this.newItem.location_id, 2);
      }

      if (this.$refs.location_works_select) {
        const locationWorks = location.locations_work_types;
        await this.$refs.location_works_select.configure(0, locationWorks);
      }

      this.noLocationDisabled = false;

      // Руководитель
      const projectUser = location.location_users.find(item => item.location_position_id === 1);

      if (projectUser) {
        this.newItem.project_id = projectUser.user_id;
        this.newItem.project_rate = projectUser.rate;

        const projectUserResult = await this.getUserById(projectUser.user_id);

        if (projectUserResult && projectUserResult.data) {
          this.newItem.project_name = projectUserResult.data.name;
        }
      } else {
        this.newItem.project_id = null;
        this.newItem.project_name = '';
        this.newItem.project_rate = null;
      }

      await this.getCustomer(location.customer_id);

      const managerUser = location.location_users.find(item => item.location_position_id === 2);

      if (managerUser) {
        this.newItem.executor = managerUser.user_id;

        const managerUserResult = await this.getUserById(managerUser.user_id);

        if (managerUserResult && managerUserResult.data) {
          this.newItem.executor_name = managerUserResult.data.name;
        }
      } else {
        this.newItem.executor = 0;
        this.newItem.executor_name = '';
      }

      this.updateExecutorRate(location);
    },
    async onClearLocation() {
      this.newItem.location_id = 0;
      this.newItem.location_name = '';
      this.newItem.customer_id = 0;
      this.newItem.customer_name = '';
      this.newItem.project_id = 0;
      this.newItem.project_name = '';
      this.newItem.executor = 0;
      this.newItem.executor_name = '';
      this.newItem.town_id = null;
      this.newItem.payment_type_int = 0;
      this.newItem.payment_first = 0;
      this.newItem.start_address = '';
      this.newItem.group_id = null;
      this.newItem.group_name = '';
      this.newItem.task_location_works = [];

      if (this.$refs.user_select) {
        await this.$refs.user_select.configure(this.newItem.executor, this.newItem.location_id, 2);
      }


      if (this.$refs.location_works_select) {
        await this.$refs.location_works_select.configure(0, []);
      }
    },
    async onSelectExecutor(userId) {
      if (userId === 0) {
        this.newItem.executor = 0;
        this.newItem.executor_name = '';
        this.newItem.executor_rate = 0;
        return;
      }

      const userResult = await this.getUserById(userId);

      if (!userResult || !userResult.data) return;

      this.newItem.executor = userResult.data.id;
      this.newItem.executor_name = userResult.data.name;
      this.updateExecutorRate();
    },
    updateExecutorRate(location = null) {
      if (location) this.location = location;

      const selectedLocation = this.location;

      if (!selectedLocation || !selectedLocation.location_users || !this.newItem.executor) {
        this.newItem.executor_rate = 0;
        return;
      }

      const locationUser = selectedLocation.location_users.find(item =>
        item.user_id === this.newItem.executor && item.location_position_id === 2
      );
      this.newItem.executor_rate = locationUser ? locationUser.rate : 0;
    },
    async getCustomer(customer_id) {
      const customerResult = await this.getCustomerById(customer_id);

      if (!customerResult || !customerResult.data) {
        this.newItem.customer_id = 0;
        this.newItem.customer_name = '';
        return;
      }

      this.newItem.customer_id = customerResult.data.id;
      this.newItem.customer_name = customerResult.data.name;
    },
    onLocationWorkSelect(locationWork) {
      if (!locationWork) {
        if (this.newItem.task_location_works.length === 1) {
          this.newItem.task_location_works.splice(0, 1);
        }
        this.newItem.payment_first = 0;
        this.newItem.unit_code = "539";
        return;
      }

      if (this.newItem.task_location_works.length === 1) {
        this.newItem.task_location_works.splice(0, 1);
      }

      this.newItem.task_location_works.push({
        task_id: this.newItem.id,
        location_id: this.location.id,
        location_work_id: locationWork.id,
        work_type_id: locationWork.work_type_id,
        work_type_name: locationWork.work_type_name,
        rate: locationWork.rate,
        unit_code: locationWork.unit_code
      });

      this.newItem.payment_first = locationWork.rate;
      this.newItem.unit_code = locationWork.unit_code;
      this.newItem.work_type_id = locationWork.work_type_id;
    },
    showWorkersSelect() {
      if (this.$refs.workers_select) {
        this.$refs.workers_select.showModalSelect();
      }
    },
    removeWorker(workerId) {
      this.newItem.task_workers = this.newItem.task_workers.filter(w => w.worker_id !== workerId);
    },
    clearAllWorkers() {
      this.newItem.task_workers = [];
    },
    onAddWorkers(workers) {
      const defaultDate = this.formatDate(new Date());
      const defaultTime = this.formatTime(new Date());

      workers.forEach(worker => {
        const exists = this.newItem.task_workers.some(tw => tw.id === worker.id);

        if (!exists) {
          const taskWorker = {
            worker_id: worker.id,
            name: worker.name,
            phone: worker.phone,
            inn: worker.inn,
            level_efficiency: worker.level_efficiency,
            price: 0.0,
            road_price: 0.0,
            start_time: defaultTime,
            end_time: defaultTime,
            total_price: 0.0,
            prepaid: 0.0,
            dinner: 0.0,
            description: '',
            start_date: this.formatServerDateTime(defaultDate, defaultTime),
            end_date: this.formatServerDateTime(defaultDate, defaultTime),
            string_start_date: defaultDate,
            string_end_date: defaultDate,
            paid: false,
            status_pay: 'unpaid',
            worker: worker,
            total_units: 0,
            task_worker_services: []
          };

          this.newItem.task_workers.push(taskWorker);
        }
      });
    },
    getTotalHours(tw) {
      let diff = ((this.getDateToServer(tw.end_date) - this.getDateToServer(tw.start_date)) / 60000 / 60);
      diff = Math.round(diff * 100) / 100;
      // Вычитаем обед
      return diff - tw.dinner;
    },
    getLevelEfficiencyStyle(value) {
      switch (value) {
        case 'high':
          return 'tag-success';
        case 'medium':
          return 'tag-warning';
        case 'low':
          return 'tag-danger';
        default:
          return '';
      }
    },
    onSelectTaskGroup(group) {
      this.newItem.group_id = group ? group.id : null;
      this.newItem.group_name = group ? group.name : '';
    },
  }
}
</script>

<style>
.custom-tabs.stepper-vertical {
  height: 100%;
  flex-grow: 1;
}

.custom-tabs.stepper-vertical .nav-tabs {
  flex-direction: column !important;
  border: none !important;
  width: 150px !important;
  padding: 0 !important;
}

.custom-tabs.stepper-vertical .nav-item {
  width: 100%;
  margin: 0 !important;
}

.custom-tabs.stepper-vertical .nav-link,
.custom-tabs.stepper-vertical .nav-link:hover,
.custom-tabs.stepper-vertical .nav-link.active {
  border: none !important;
  border-bottom: none !important;
  padding: 0 0 65px 0 !important;
  background: transparent !important;
  display: block !important;
}

.custom-tabs.stepper-vertical > .tab-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100% !important;
}

.custom-tabs.stepper-vertical > .tab-content > .tab-pane.active {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
}

.step-item.is-completed .step-title {
  color: #212529;
}

.step-icon {
  width: 24px;
  height: 24px;
  background: #E9ECEF;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #adb5bd;
  flex-shrink: 0;
  z-index: 2;
}

.step-icon.is-completed {
  background: #ECF7F1 !important;
  color: #1A9167 !important;
  border: none;
}

.stepper-vertical .nav-link.active .step-icon {
  background: #1A9167;
  color: #fff;
}

.stepper-vertical .nav-item:not(:last-child) .step-item::after {
  content: '';
  position: absolute;
  top: 31px;
  left: 11px;
  width: 2px;
  height: 51px;
  background: #E9ECEF;
  z-index: 1;
}

.step-label {
  padding-top: 2px;
  white-space: normal;
}

.step-title {
  font-size: 14px;
  line-height: 20px;
  color: #6c757d;
  transition: color 0.3s;
}

.stepper-vertical .nav-link.active .step-title {
  color: #1A9167;
  font-weight: 600;
}

.custom-tags-input {
  height: auto !important;;
  min-height: 60px;
  padding: 8px 30px 8px 8px;
}

.tags-wrapper {
  gap: 4px;
  width: 100%;
}

.clear-all-icon {
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  z-index: 2;
  color: #adb5bd;
  cursor: pointer;
  position: absolute;
}

.clear-all-icon:hover {
  color: #495057;
}

.custom-select-filters {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 12px;
  width: auto;
  min-width: 80px;
  height: 32px;
  background: #FFFFFF;
  border: 1px solid #DCE2EA;
  border-radius: 6px;
  outline: none;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
}
.custom-select-filters:focus {
  border-color: #a0b3c6;
}

.summary-title {
  font-size: 16px;
  color: #212529;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ECEEF1;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  flex: 0 0 160px;
  font-size: 14px;
  color: #6C757D;
  white-space: nowrap;
}

.summary-value {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #212529;
  text-align: right;
  word-break: break-word;
}

/* onboarding */
.custom-onboarding-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1055 !important;
  pointer-events: none;
}

.custom-hint-popover {
  z-index: 1070 !important;
  pointer-events: auto !important;
  border-radius: 12px;
  padding: 12px;
}

.task-group-badge {
  font-weight: 500;
  background-color: #ECF7F1 !important;
  color: #212529;
  border: 1px solid rgba(26, 145, 103, 0.3);
}

#modal-task-group-create .modal-dialog {
  max-width: 100% !important;
  max-height: 100% !important;
  height: 100% !important;
  margin: 0 !important;
}

.modal-fullscreen {
  max-height: 100% !important;
  height: 100% !important;
}

.modal-body-fullscreen {
  padding-bottom: 0 !important;
  display: flex !important;
  flex-direction: column !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Tasks/TaskGroupCreate.vue